<template>
  <div v-if="loading" class="left-0 top-0 h-full w-full py-20">
    <TheLoader />
  </div>
  <div v-else>
    <div v-if="isAuthorized">
      <ChartTab
        v-if="data.length"
        :subtitle="`${market.name} Capacities`"
        :product="market.name"
        :check-class-colors="checkClassColors"
        :circle-class-colors="circleClassColors"
        capacity-unit="tpa"
        :colors="colors"
        :hide-y-label="false"
        :is-lithium-ion-batteries="false"
        :truncate-labels="false"
        :raw-data="data"
        :regions="regions"
      />
    </div>

    <OverviewTab
      v-else
      :subtitle="`${market.name} Capacities`"
      :descriptions="{
        title: null,
        texts: [
          'Our Price Assessments draw on Benchmark’s proprietary databases, tracking every global production facility, capacity expansions, technology developments and industry partnerships to provide clarity to the complex lithium ion battery landscape',
        ],
      }"
      :packages="{
        title: `${PRICE_ASSESSMENTS.singularName} Package`,
        services: [
          {
            icon: 'services/icon-chart-line',
            description:
              'Access to exclusive Cathode data, with company and regional capacity, and supply demand/analytics',
          },
          {
            icon: 'services/icon-report',
            description:
              'Monthly report providing Cathode prices and insights to reveal the direction of the midstream battery sector',
          },
          {
            icon: 'services/icon-presentation',
            description:
              'Produce custom made representations of data to download for projects and presentations.',
          },
          {
            icon: 'services/icon-consultancy',
            description:
              'Create your own custom pricing charts using our bespoke data.',
          },
          {
            icon: 'services/icon-global',
            description:
              'A global perspective on the evolution of the Cathode industry.',
          },
          {
            icon: 'services/icon-idea',
            description:
              'Our goal is to help you understand the context of our data, not just give you the numbers.',
          },
        ],
        classes: '',
      }"
      :subscribe="{
        checkboxes,
        methodology: methodologyFileInfo,
      }"
    >
      <template #description>
        <div
          class="flex flex-row items-center justify-between gap-x-2 pt-4 xl:pt-16"
        >
          <img
            src="https://res.cloudinary.com/benchmarkminerals/image/upload/c_fill,q_auto,g_auto,w_420,f_auto/v1719010871/assets/market/cathode/capacity-database/cathode-capacities-description-desktop_jhnnmm.png?_i=AA"
            class="max-h-[250px] min-h-[250px] w-full rounded-lg bg-p-500 object-cover lg:w-[60%] xl:max-h-[240px] xl:min-h-[240px] xl:w-[68%]"
            alt=""
            aria-hidden
          />
          <img
            src="https://res.cloudinary.com/benchmarkminerals/image/upload/c_fill,q_auto,g_auto,w_240,f_auto/v1719010870/assets/market/cathode/capacity-database/cathode-capacities-description-desktop-2_tobsyl.png?_i=AA"
            class="hidden max-h-[250px] min-h-[250px] w-full rounded-lg bg-p-500 object-cover sm:block sm:w-[40%] xl:max-h-[240px] xl:min-h-[240px] xl:w-[30%]"
            alt=""
            aria-hidden
          />
        </div>
      </template>
    </OverviewTab>
  </div>
</template>

<script setup lang="ts">
import ChartTab from '~/components/capacity-database/ChartTab.vue'
import OverviewTab from '~/components/capacity-database/OverviewTab.vue'

import { CATHODE, PRICE_ASSESSMENTS } from '~/constants/services/constants'
import {
  CAPACITY_DATABASE_SUBSCRIPTIONS,
  METHODOLOGY_FILE_INFO,
} from '~/src/constants/capacity-database/common'

import type { TFileDownloadInfo } from '~/src/types/download'
import { useAuthentication } from '~/composables/useAuthentication'
import { useCapacityDatabaseData } from '~/composables/useCapacityDatabaseData'

/*-------------------------------------
  Meta
-------------------------------------*/
definePageMeta({ isTab: true })

/*-------------------------------------
  Constants: Chart Tab
-------------------------------------*/
const market = CATHODE

const colors = ['#FDBD00', '#00325C', '#005BA9', '#2699FC']

const circleClassColors = ['bg-grey-900', 'bg-white', 'bg-white', 'bg-grey-900']

const checkClassColors = [
  'text-grey-900',
  'text-white',
  'text-white',
  'text-grey-900',
]

/*-------------------------------------
  Constants: Overview Tab
-------------------------------------*/
const subscription = CAPACITY_DATABASE_SUBSCRIPTIONS[market.slug]
const checkboxes = [market.checkbox, PRICE_ASSESSMENTS.checkbox]

const { $config } = useNuxtApp()

const METHODOLOGY_ENV_KEY = METHODOLOGY_FILE_INFO[market.slug].EnvKey
const methodologyFileInfo: TFileDownloadInfo = {
  ...METHODOLOGY_FILE_INFO[market.slug],
  ID: $config.public[METHODOLOGY_ENV_KEY],
}

/*-------------------------------------
  Props & Refs
-------------------------------------*/
const { loading, isAuthorized, data, regions, fetchChartDataIfAuthorized } =
  useCapacityDatabaseData({
    capacitySlug: market.slug,
    subscription,
  })

const emit = defineEmits<{
  'set-hero-ctas': [methodology: TFileDownloadInfo, checkboxes: string[]]
}>()

/*-------------------------------------
  Lifecycle
-------------------------------------*/
onMounted(() => {
  emit('set-hero-ctas', methodologyFileInfo, checkboxes)
  fetchChartDataIfAuthorized()
})

useAuthentication(() => {
  fetchChartDataIfAuthorized()
})

/*-------------------------------------
  SEO
-------------------------------------*/
const { getSeoInfo } = useSeo()
const seo = getSeoInfo({
  title: `${PRICE_ASSESSMENTS.name}: ${market.name}`,
  description: market.seo.priceAssessments.capacities,
})
useHead(seo)
</script>
